import { api } from '@/api';
import {
  TARA_CALLBACK_ROUTE,
  TARA_LOGIN_PATH,
  TARA_PROFILE_NOT_FOUND_ERROR,
  TARA_REGISTER_PATH,
  TARA_RESULT_PATH,
} from '@/features/auth/taraConfig';
import type {
  LoginRequestBody,
  LoginResponse,
  LogoutRequestBody,
  TaraExchangeResult,
  TaraRegisterRequestBody,
} from '@/types/auth';
import type {
  CreateCustomerProfileRequestBodyDTO,
  CustomerProfileDetailsResponseDTO,
} from '@/types/dto/agent';
import axios from 'axios';

const BASE_URL = `${import.meta.env.VITE_APP_PROXY_TARGET}`;

export const authenticateUser = async (
  body: LoginRequestBody
): Promise<LoginResponse> => {
  const response = await axios.post(`${BASE_URL}/auth/token`, body, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
  return response.data;
};

export const registerUser = async (
  body: CreateCustomerProfileRequestBodyDTO
): Promise<CustomerProfileDetailsResponseDTO> => {
  const response = await axios.post(`${BASE_URL}/auth/register`, body);
  return response.data;
};

export const registerWithTara = async (
  body: TaraRegisterRequestBody
): Promise<void> => {
  await axios.post(`${BASE_URL}${TARA_REGISTER_PATH}`, body);
};

export const verifyRegistration = async (
  unverifiedCustomerProfileId: string,
  verificationHash: string
): Promise<void> => {
  await axios.post(`${BASE_URL}/auth/verify`, {
    unverifiedCustomerProfileId,
    verificationHash,
  });
};

export const getCustomerProfile =
  async (): Promise<CustomerProfileDetailsResponseDTO> => {
    return (await api.agentApi.CustomerProfile_GetCustomerProfileDetails())
      .data;
  };

export const revokeToken = async (body: LogoutRequestBody): Promise<void> => {
  await axios.post(`${BASE_URL}/auth/revoke`, body, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
};

/**
 * Builds the TARA login-initiation URL. The BE reads `return_uri`, builds the
 * TARA redirect, and eventually sends the browser back to that URI with a
 * one-time login-result token.
 *
 * Security note (BE concern, flag if missing): `return_uri` must be
 * pre-registered/allowlisted server-side to avoid an open-redirect.
 */
export const buildTaraLoginUrl = (): string => {
  const returnUri = `${window.location.origin}${TARA_CALLBACK_ROUTE}`;
  const params = new URLSearchParams({ return_uri: returnUri });
  return `${BASE_URL}${TARA_LOGIN_PATH}?${params.toString()}`;
};

/** Full-page navigation — we are leaving the SPA for TARA. */
export const startTaraLogin = (): void => {
  window.location.assign(buildTaraLoginUrl());
};

/**
 * Exchanges the intermediate TARA login-result token. This is the fork point:
 * a customer profile either exists (→ tokens) or does not (→ registration).
 * Both BE shapes are normalized into a discriminated union so callers never
 * parse OAuth error strings.
 *
 * `validateStatus` is widened because the "no profile" case comes back as an
 * OAuth-style error response (non-2xx) whose body we still need to read.
 */
export const exchangeTaraResult = async (
  loginResultToken: string
): Promise<TaraExchangeResult> => {
  const response = await axios.post<{
    tokens_ref?: string;
    expires_in?: number;
    token_type?: string;
    error_description?: string;
    registration_token?: string;
    customer_info?: {
      first_name?: string;
      firstName?: string;
      last_name?: string;
      lastName?: string;
      birth_date?: string;
      birthDate?: string;
    } | null;
  }>(
    `${BASE_URL}${TARA_RESULT_PATH}`,
    new URLSearchParams({ login_result_token: loginResultToken }),
    {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      validateStatus: () => true,
    }
  );

  const data = response.data ?? {};

  // Success shape: the proxy returns an opaque tokens_ref (BFF model).
  if (data.tokens_ref) {
    return {
      kind: 'authenticated',
      response: {
        tokens_ref: data.tokens_ref,
        expires_in: data.expires_in ?? 0,
        token_type: data.token_type,
      },
    };
  }

  // "No profile" shape: OAuth-style error carrying an opaque registration_token.
  // Treat the token as a pass-through — do not decode or derive identity from
  // it; the BE reads the person's identity out of it server-side.
  if (
    data.error_description === TARA_PROFILE_NOT_FOUND_ERROR &&
    data.registration_token
  ) {
    const info =
      data.customer_info && typeof data.customer_info === 'object'
        ? data.customer_info
        : undefined;
    const birthDate = info?.birth_date ?? info?.birthDate;

    return {
      kind: 'needs_registration',
      registrationToken: data.registration_token,
      firstName: info?.first_name ?? info?.firstName,
      lastName: info?.last_name ?? info?.lastName,
      birthDate: birthDate ? birthDate.split('T')[0] : undefined,
    };
  }

  // Anything else is a genuine failure (handled by the caller's catch).
  throw new Error(data.error_description || 'tara-result failed');
};
